import { MapPin, Phone, Mail, Clock, ArrowUpRight, Send, MessageCircle } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/motion";
import { IMAGES, CLUB, gmailCompose } from "@/data/site";

const CONTACTS = [
  { id: "indirizzo", icon: MapPin, label: "Indirizzo", value: `${CLUB.address}, ${CLUB.city}`, href: `https://www.google.com/maps?q=${encodeURIComponent(CLUB.mapsQuery)}` },
  { id: "telefono", icon: Phone, label: "Telefono", value: CLUB.phone, href: CLUB.phoneHref },
  { id: "whatsapp", icon: MessageCircle, label: "WhatsApp", value: CLUB.whatsapp, href: CLUB.whatsappHref },
];

export default function DoveSiamo() {
  const gmailUrl = gmailCompose(
    "Richiesta prova gratuita - CAB",
    "Ciao Club Atletico Bologna,\n\nvorrei prenotare una prova gratuita.\n\nNome:\nDisciplina di interesse (Lotta / Pesistica):\nTelefono:\n\nGrazie!"
  );

  return (
    <div data-testid="dove-siamo-page">
      <PageHero
        image={IMAGES.gym}
        overline="Contatti & Mappa"
        titleLines={["Dove", "siamo"]}
        subtitle="Ci trovi in via Aristotile Fioravanti 22, presso Piazza Lucio Dalla, nel cuore di Bologna. Vieni a conoscerci: la prima prova è sempre gratuita."
      />

      {/* Contact cards */}
      <section className="bg-cream py-20 md:py-28" data-testid="contatti-info">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CONTACTS.map(({ id, icon: Icon, label, value, href }) => (
              <StaggerItem key={id}>
                <a
                  href={href}
                  target={id === "indirizzo" || id === "whatsapp" ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={`contact-${id}`}
                  className="group flex h-full items-start gap-4 rounded-2xl border border-ink/10 bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-cab/40 hover:shadow-[0_20px_50px_rgba(62,106,158,0.12)]"
                >
                  <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                    <Icon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cab">
                      {label}
                    </p>
                    <p className="mt-1 font-medium text-ink">{value}</p>
                  </div>
                </a>
              </StaggerItem>
            ))}
            <StaggerItem>
              <div className="flex h-full items-start gap-4 rounded-2xl border border-ink/10 bg-white p-7">
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cab text-cream">
                  <Mail className="h-6 w-6" />
                </span>
                <div className="min-w-0">
                  <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-cab">Email</p>
                  <a
                    href={`mailto:${CLUB.email}`}
                    data-testid="contact-email"
                    className="mt-1 block break-all font-medium text-ink transition-colors hover:text-cab"
                  >
                    {CLUB.email}
                  </a>
                  <a
                    href={`mailto:${CLUB.pec}`}
                    data-testid="contact-pec"
                    className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-cab"
                  >
                    <span className="break-all">{CLUB.pec}</span>
                    <span className="inline-block whitespace-nowrap rounded bg-cab/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cab">
                      PEC
                    </span>
                  </a>
                </div>
              </div>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      {/* Map + hours */}
      <section className="bg-white py-8 md:py-12" data-testid="contatti-mappa">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-5 md:px-8 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <div className="overflow-hidden rounded-2xl border border-ink/10 shadow-[0_20px_50px_rgba(62,106,158,0.1)]">
              <iframe
                title="Mappa Club Atletico Bologna"
                data-testid="google-map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(CLUB.mapsQuery)}&z=14&output=embed`}
                className="h-[360px] w-full md:h-[520px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>

          <div className="lg:col-span-2">
            <SectionHeading overline="Quando siamo aperti" title="Orari" />
            <div className="mt-8 space-y-6">
              <div className="rounded-2xl border border-ink/10 bg-cream p-6">
                <h3 className="flex items-center gap-2 font-display text-lg font-extrabold uppercase tracking-tight text-ink">
                  <Clock className="h-5 w-5 text-cab" /> Palestra
                </h3>
                <ul className="mt-4 space-y-3">
                  {CLUB.hours.map((h) => (
                    <li key={h.day} className="flex justify-between gap-4 text-[15px]">
                      <span className="text-ink">{h.day}</span>
                      <span className="font-medium text-muted-foreground">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-ink/10 bg-cab-deep p-6 text-cream">
                <h3 className="flex items-center gap-2 font-display text-lg font-extrabold uppercase tracking-tight">
                  <MessageCircle className="h-5 w-5 text-cab-light" /> Contatti rapidi
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-cream/80">
                  La segreteria è attiva negli orari di apertura della palestra. Per informazioni immediate scrivici su WhatsApp o chiamaci.
                </p>
                <div className="mt-5 flex flex-col gap-3">
                  <a
                    href={CLUB.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hours-whatsapp"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-5 py-3 font-bold text-white transition-transform duration-300 hover:-translate-y-0.5"
                  >
                    <MessageCircle className="h-4 w-4" /> {CLUB.whatsapp}
                  </a>
                  <a
                    href={CLUB.phoneHref}
                    data-testid="hours-phone"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-cream/30 px-5 py-3 font-bold text-cream transition-colors hover:bg-cream/10"
                  >
                    <Phone className="h-4 w-4" /> {CLUB.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gmail banner (no form) */}
      <section className="bg-white py-20 md:py-28" data-testid="contatti-banner">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-cab-deep px-6 py-14 text-center md:px-16 md:py-20">
              <div className="absolute inset-0 opacity-15">
                <img src={IMAGES.heroLifting} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </div>
              <div className="absolute inset-0 grain" />
              <div className="relative">
                <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-cab-light">
                  Scrivici in un click
                </span>
                <h2 className="mx-auto mt-5 max-w-3xl font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-cream sm:text-5xl">
                  Richiedi la tua prova gratuita
                </h2>
                <p className="mx-auto mt-5 max-w-xl text-base text-cream/75 md:text-lg">
                  Clicca qui sotto: si aprirà una email già pronta verso la nostra casella.
                  Raccontaci cosa ti interessa e ti risponderemo al più presto.
                </p>
                <a
                  href={gmailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="gmail-banner-button"
                  className="group mt-9 inline-flex items-center gap-2.5 rounded-md bg-cream px-8 py-4 font-bold text-cab-deep transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <Send className="h-4 w-4" />
                  Scrivici su Gmail
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <p className="mt-5 text-sm text-cream/60">
                  Oppure scrivici a{" "}
                  <a href={`mailto:${CLUB.email}`} data-testid="banner-mailto" className="font-medium text-cream underline underline-offset-4">
                    {CLUB.email}
                  </a>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
